import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	OnInit,
	Renderer2,
	ViewChild
} from '@angular/core';
import {filter, take, takeUntil} from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import * as objectPath from 'object-path';
// Layout
import {
  LayoutConfigService,
  MenuAsideService,
  MenuConfigService,
  MenuOptions,
  OffcanvasOptions, TranslationService
} from '../../../core/_base/layout';
import { HtmlClassService } from '../html-class.service';
import { TranslateService } from '@ngx-translate/core';
import { AngularFirestore } from '@angular/fire/firestore';
import {select, Store} from '@ngrx/store';
import {currentUser} from '../../../core/charts/_selectors/charts.selectors';
import {AppState} from '../../../core/reducers';
import {Subject} from 'rxjs';
import {FirebaseService} from '../../../services/firebase.service';
import {SubscriptionsService} from '../../../services/subscriptions.service';
import {MatDialog} from "@angular/material/dialog";
import {SubscriptionsDialogComponent} from "./subscriptions-dialog/subscriptions-dialog.component";

@Component({
  selector: 'kt-aside-left',
  templateUrl: './aside-left.component.html',
  styleUrls: ['./aside-left.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AsideLeftComponent implements OnInit, AfterViewInit {
  private offcanvas: any;

  @ViewChild('asideMenuOffcanvas', {static: true}) asideMenuOffcanvas: ElementRef;
  @ViewChild('asideMenu', {static: true}) asideMenu: ElementRef;

  asideLogo = '';
  asideClasses = '';
  currentRouteUrl = '';
  insideTm: any;
  outsideTm: any;

  menuCanvasOptions: OffcanvasOptions = {
    baseClass: 'aside',
    overlay: true,
    closeBy: 'kt_aside_close_btn',
    toggleBy: {
      target: 'kt_aside_mobile_toggle',
      state: 'mobile-toggle-active'
    }
  };

  menuOptions: MenuOptions = {
    // submenu setup
    submenu: {
      desktop: {
        // by default the menu mode set to accordion in desktop mode
        default: 'dropdown',
      },
      tablet: 'accordion', // menu set to accordion in tablet mode
      mobile: 'accordion' // menu set to accordion in mobile mode
    },

    // accordion setup
    accordion: {
      expandAll: false // allow having multiple expanded accordions in the menu
    }
  };
  details: any;
  userDetails = new Subject();
  hideMenus = false;

  /**
   * Component Constructor
   *
   * param htmlClassService: HtmlClassService
   * param menuAsideService
   * param layoutConfigService: LayoutConfigService
   * param router: Router
   * param render: Renderer2
   * param cdr: ChangeDetectorRef
   */
  constructor(
    public htmlClassService: HtmlClassService,
    public menuAsideService: MenuAsideService,
    public layoutConfigService: LayoutConfigService,
    private router: Router,
    private render: Renderer2,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private afs: AngularFirestore,
    private store: Store<AppState>,
    private firebase: FirebaseService,
    private menuConfigService: MenuConfigService,
    private subscriptions: SubscriptionsService,
    private dialog: MatDialog,
    private translationService: TranslationService
  ) {
  }

  ngAfterViewInit(): void {
    this.store.pipe(select(currentUser)).pipe(takeUntil(this.userDetails)).subscribe((user) => {
      if (user && user.id) {
        this.userDetails.next('');
        this.userDetails.complete();
        this.firebase.getDevelopersList().pipe(take(1)).subscribe((developers: any[]) => {
          const isDeveloper = developers.some(developer => developer.developerId === user.id);
          if (isDeveloper) {
            const devMenu = {
              bullet: 'dot',
              icon: './assets/media/svg/submenu-icons/database.svg',
              page: '/users-data',
              root: true,
              title: 'Data',
              translate: 'MENU.USERS_DATA'
            }
            const menuItems: any[] = objectPath.get(this.menuConfigService.getMenus(), 'aside.items');
            menuItems.push(devMenu);
            this.menuAsideService.menuList$.next(menuItems);
            this.cdr.markForCheck();
          }
          if (user.supervisorSubscriptionNeeded) {
            this.hideMenus = true;
            this.cdr.markForCheck();
            this.subscriptions.checkUserSubscriptions(user).subscribe((subscriptions:any) => {
              if (!subscriptions || !subscriptions.length) {
                this.hideMenus = false;
                this.cdr.markForCheck();
                this.hideSupervisorMenus();
                this.showSubscriptionsDialog();
              }
            });
          }
        });
      }
    });
  }
  hideSupervisorMenus() {
    const menuItems: any[] = [
      {
        bullet: 'dot',
        icon: './assets/media/svg/submenu-icons/profile.svg',
        page: '/profile',
        root: true,
        title: 'Profile',
        translate: 'MENU.PROFILE'
      },
      {
        icon:'./assets/media/svg/submenu-icons/archive.svg',
        root: true,
        submenu: [{
          bullet:'dot',
          page: '/my-archive',
          title: 'My Archive',
          translate: 'MENU.MY_ARCHIVE'
        }],
        title: 'Archive',
        translate: 'MENU.ARCHIVE'
      },
      {
        icon: './assets/media/svg/submenu-icons/line-view.svg',
        root: true,
        submenu: [
          {
            bullet: 'dot',
            page: '/live-view',
            title: 'Live view',
            translate: 'MENU.LIVE_VIEW'
          }
        ],
        title: 'Charts',
        translate: 'MENU.CHARTS'
      }
    ]
    this.menuAsideService.menuList$.next(menuItems);
    this.cdr.markForCheck();
  }
  showSubscriptionsDialog() {
    this.dialog.open(SubscriptionsDialogComponent)
  }

  ngOnInit() {
    this.afs
      .collection('links')
      .snapshotChanges()
      .subscribe((values) => {
        const obj = {};
        values.forEach((d: any) => {
          obj[d.payload.doc.id] = {
            id: d.payload.doc.id,
            ...d.payload.doc.data(),
          };
        });
        this.details = obj;
      });
    this.currentRouteUrl = this.router.url.split(/[?#]/)[0];

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(event => {
        this.currentRouteUrl = this.router.url.split(/[?#]/)[0];
        this.mobileMenuClose();
        this.cdr.markForCheck();
      });



    const config = this.layoutConfigService.getConfig();

    if (objectPath.get(config, 'aside.menu.dropdown')) {
      this.render.setAttribute(this.asideMenu.nativeElement, 'data-ktmenu-dropdown', '1');
      // tslint:disable-next-line:max-line-length
      this.render.setAttribute(this.asideMenu.nativeElement, 'data-ktmenu-dropdown-timeout', objectPath.get(config, 'aside.menu.submenu.dropdown.hover-timeout'));
    }

    this.asideClasses = this.htmlClassService.getClasses('aside', true).toString();
    this.asideLogo = this.getAsideLogo();
    setTimeout(() => {
      this.offcanvas = new KTOffcanvas(this.asideMenuOffcanvas.nativeElement, this.menuCanvasOptions);
    });
  }

  getAsideLogo() {
    let result = 'logo-light.png';
    const brandSelfTheme = this.layoutConfigService.getConfig('brand.self.theme') || '';
    if (brandSelfTheme === 'light') {
      result = 'logo-dark.png';
    }
    return `./assets/media/logos/${result}`;
  }

  /**
   * Check Menu is active
   * @param item: any
   */
  isMenuItemIsActive(item): boolean {
    if (item.submenu) {
      return this.isMenuRootItemIsActive(item);
    }

    if (!item.page) {
      return false;
    }

    return this.currentRouteUrl.indexOf(item.page) !== -1;
  }

  /**
   * Check Menu Root Item is active
   * @param item: any
   */
  isMenuRootItemIsActive(item): boolean {
    let result = false;

    for (const subItem of item.submenu) {
      result = this.isMenuItemIsActive(subItem);
      if (result) {
        return true;
      }
    }
    return false;
  }

  /**
   * Use for fixed left aside menu, to show menu on mouseenter event.
   * @param e Event
   */
  mouseEnter(e: Event) {
    // check if the left aside menu is fixed
    if (document.body.classList.contains('aside-fixed')) {
      if (this.outsideTm) {
        clearTimeout(this.outsideTm);
        this.outsideTm = null;
      }

      this.insideTm = setTimeout(() => {
        // if the left aside menu is minimized
        if (document.body.classList.contains('aside-minimize') && KTUtil.isInResponsiveRange('desktop')) {
          // show the left aside menu
          this.render.removeClass(document.body, 'aside-minimize');
          this.render.addClass(document.body, 'aside-minimize-hover');
        }
      }, 50);
    }
  }

  /**
   * Use for fixed left aside menu, to show menu on mouseenter event.
   * @param e Event
   */
  mouseLeave(e: Event) {
    if (document.body.classList.contains('aside-fixed')) {
      if (this.insideTm) {
        clearTimeout(this.insideTm);
        this.insideTm = null;
      }

      this.outsideTm = setTimeout(() => {
        // if the left aside menu is expand
        if (document.body.classList.contains('aside-minimize-hover') && KTUtil.isInResponsiveRange('desktop')) {
          // hide back the left aside menu
          this.render.removeClass(document.body, 'aside-minimize-hover');
          this.render.addClass(document.body, 'aside-minimize');
        }
      }, 100);
    }
  }

  /**
   * Returns Submenu CSS Class Name
   * @param item: any
   */
  getItemCssClasses(item) {
    let classes = 'menu-item';

    if (objectPath.get(item, 'submenu')) {
      classes += ' menu-item-submenu';
    }

    if (!item.submenu && this.isMenuItemIsActive(item)) {
      classes += ' menu-item-active menu-item-here';
    }

    if (item.submenu && this.isMenuItemIsActive(item)) {
      classes += ' menu-item-open menu-item-here';
    }

    // custom class for menu item
    const customClass = objectPath.get(item, 'custom-class');
    if (customClass) {
      classes += ' ' + customClass;
    }

    if (objectPath.get(item, 'icon-only')) {
      classes += ' menu-item-icon-only';
    }

    return classes;
  }

  getItemAttrSubmenuToggle(item) {
    let toggle = 'hover';
    if (objectPath.get(item, 'toggle') === 'click') {
      toggle = 'click';
    } else if (objectPath.get(item, 'submenu.type') === 'tabs') {
      toggle = 'tabs';
    } else {
      // submenu toggle default to 'hover'
    }

    return toggle;
  }


  mobileMenuClose() {
    if (KTUtil.isBreakpointDown('lg') && this.offcanvas) { // Tablet and mobile mode
      this.offcanvas.hide(); // Hide offcanvas after general link click
    }
  }

  onNavigate(linkType) {
    let url;
    if (linkType === 'dashboard') {
      url = this.details[this.translationService.getSelectedLanguage()].dashboard;
    } else if (linkType === 'features') {
      url = this.details[this.translationService.getSelectedLanguage()].features;
    }
    window.open(url, '_blank');
  }
  isAsideMinimized() {
    const aside = document.getElementById('kt_aside').getBoundingClientRect().width;
    const isMinimized = document.body.classList.contains('aside-minimize-hover');
    if (aside > 70 && !isMinimized) {
      return {
        'flex-direction': 'row',
      }
    } else {
      return {
        'flex-direction': 'column',
      }
    }
  }
  handleFontSize() {
    const aside = document.getElementById('kt_aside').getBoundingClientRect().width;
    const isMinimized = document.body.classList.contains('aside-minimize-hover');
    if (aside > 70 && !isMinimized) {
      return {
        'font-size': '12px',
      }
    } else {
      return {
        'font-size': '10px',
      }
    }
  }
  menuTranslate(key) {
    return this.translate.instant(key);
  }
}
